import { Link } from "react-router-dom";
import { Header } from "@/components/landing/Header";
import { Footer } from "@/components/landing/Footer";
import { SeoHead } from "@/components/seo/SeoHead";
import { StructuredData } from "@/components/seo/StructuredData";
import { getBaseUrl, buildBreadcrumbSchema } from "@/lib/seo";
import { Button } from "@/components/ui/button";
import { Bot, MessageCircle, Shield, Zap, ArrowRight } from "lucide-react";

const SOBRE_TITLE = "Sobre a FabChat | Plataforma de agentes de IA para WhatsApp e Instagram";
const SOBRE_DESCRIPTION =
  "Conheça a FabChat: plataforma de agentes de IA para WhatsApp e Instagram com CRM integrado para atendimento, vendas e qualificação de leads.";

const breadcrumbItems = [
  { name: "Início", path: "/" },
  { name: "Sobre", path: "/sobre" },
];

type SobreProps = {
  canonicalPath?: string;
};

export default function Sobre({ canonicalPath = "/sobre" }: SobreProps) {
  const baseUrl = getBaseUrl();
  const breadcrumbSchema = buildBreadcrumbSchema(baseUrl, breadcrumbItems);

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <SeoHead
        title={SOBRE_TITLE}
        description={SOBRE_DESCRIPTION}
        canonicalPath={canonicalPath}
      />
      <StructuredData data={breadcrumbSchema} />
      <Header />

      <main className="flex-1">
        <section className="container mx-auto px-4 py-16 md:py-24 max-w-4xl">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Sobre a FabChat</h1>
          <p className="text-lg text-muted-foreground mb-12">
            Quem somos, o que fazemos e por que acreditamos em atendimento inteligente.
          </p>

          <section className="mb-12">
            <h2 className="text-2xl font-semibold mb-4">Quem é a FabChat</h2>
            <p className="text-muted-foreground mb-4">
              A FabChat é uma plataforma que permite a negócios criar e operar agentes de inteligência artificial conectados ao WhatsApp e ao Instagram. Nosso foco é tornar a automação de vendas, suporte e atendimento acessível e prática, para que empresas possam escalar o contato com clientes sem perder qualidade.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-semibold mb-4">Missão e proposta de valor</h2>
            <p className="text-muted-foreground mb-4">
              Queremos que você automatize conversas com inteligência: respostas coerentes, horários configuráveis e integração com seus canais. A proposta é oferecer uma ferramenta que une IA conversacional, gestão de leads e canais como WhatsApp e Instagram em um só lugar, com foco em simplicidade e resultado.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-semibold mb-4">O que a plataforma faz</h2>
            <p className="text-muted-foreground mb-4">
              Na FabChat você configura agentes de IA que respondem no WhatsApp e no Instagram, gerencia leads em um CRM integrado, agenda compromissos (com integração a calendários) e acompanha conversas em um único painel. A plataforma centraliza o atendimento e permite que você defina regras, tom de voz e limites de uso conforme sua operação.
            </p>
            <p className="text-muted-foreground">
              Conheça as páginas principais: <Link to="/whatsapp/agente-ia" className="text-primary hover:underline">agente de IA para WhatsApp</Link>, <Link to="/instagram/agente-ia" className="text-primary hover:underline">agente de IA para Instagram</Link> e <Link to="/integracoes/crm" className="text-primary hover:underline">CRM integrado</Link>.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-semibold mb-4">Diferenciais</h2>
            <ul className="space-y-4 text-muted-foreground">
              <li className="flex gap-3">
                <MessageCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                <span><strong className="text-foreground">Canais integrados:</strong> WhatsApp e Instagram no mesmo fluxo de atendimento e gestão de leads.</span>
              </li>
              <li className="flex gap-3">
                <Bot className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                <span><strong className="text-foreground">Agentes configuráveis:</strong> Defina comportamento, horários e respostas da IA de forma visual.</span>
              </li>
              <li className="flex gap-3">
                <Zap className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                <span><strong className="text-foreground">CRM e funil:</strong> Leads organizados por estágio, com histórico de conversas e anotações.</span>
              </li>
              <li className="flex gap-3">
                <Shield className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                <span><strong className="text-foreground">Privacidade e transparência:</strong> Políticas de privacidade, termos e cookies disponíveis e alinhados à LGPD.</span>
              </li>
            </ul>
          </section>

          <section className="flex flex-col sm:flex-row gap-4 pt-6">
            <Button asChild size="lg" className="gap-2">
              <Link to="/planos">
                Ver planos <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="gap-2">
              <Link to="/contato">Falar com a gente</Link>
            </Button>
          </section>
        </section>
      </main>

      <Footer />
    </div>
  );
}
